import {
  AppVersion,
  Colors,
  getMediaDevices,
  PopupButtonType,
  PopupIconType,
  PopupOnCloseData,
  showPopup,
  SettingsForm,
  SettingsFormRef,
  SettingsFormChangeData,
} from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import deepEqual from "deep-equal";
import React, { ForwardedRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from "react";
import { SETTINGS_DEFAULT_KEY } from "../../helpers/storage";
import {
  checkRemoteSettingsUrl,
  checkRemoteVariablesUrl,
  countSettingsVariableOccurrences,
  createSettingsConfig,
  deleteSettingsConfig,
  encodeBase64Url,
  getRemoteSettingsStatus,
  getRemoteVariablesStatus,
  getSettingsConfigData,
  getSettingsConfigResolvedData,
  getSettingsConfigSource,
  getSettingsConfigVariables,
  hasSettingsPlaceholder,
  listSettingsConfigs,
  loadRemoteSettingsConfig,
  loadRemoteSettingsVariables,
  resolveActiveSettingsKey,
  saveSettingsConfigData,
  selectSettingsConfig,
  setSettingsConfigSource,
  setSettingsConfigVariables,
  setSettingsConfigVariablesUrl,
  Settings,
  SettingsConfigInfo,
  SettingsConfigMode,
  SettingsVariable,
  subscribeRemoteSettingsStatus,
} from "../../helpers/settings";
import { validateCompositionSettings } from "../../helpers/composition";
import { validateEmissionSettings } from "../../helpers/emission";
import { validateSdpFormatterSettings } from "../../helpers/sdp";
import { toastError, toastSuccess } from "../../helpers/toast";
import { useRequestAdminPin } from "../../hooks/utils";
import {
  settingsSchema,
  SETTINGS_SCHEMA_VERSION,
  exportSettings,
  importSettings,
  importSettingsFromText,
  importSettingsFromUrl,
  ImportResult,
  prepareSettingsForForm,
  restoreSettingsFromForm,
  sanitizeSettings,
} from "../../helpers/settingsSchema";
import styles from "./SettingsPopup.module.css";
import workBoxStyles from "../workBoxes/WorkBox.module.css";
import Versions from "./Versions";
import { BoxOverlay, BoxContainer, Dropdown, OverlayContainer, Button, PrimaryButton, ReactIcons, Switch } from "@kalyzee/kalyzee-ui";
import { addPopup, removePopup } from "../../helpers/popup";
import { formatJsonParseError, syntaxHighlightJson } from "../../helpers/json";
import ActionMenu from "./ActionMenu";
import JsonEditor from "./JsonEditor";

// ---- Small inline icons (feather-style) for the action menus ----

const iconProps: React.SVGProps<SVGSVGElement> = {
  width: 15,
  height: 15,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconClipboard = () => (
  <svg {...iconProps}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const IconDownload = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconFile = () => (
  <svg {...iconProps}>
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="13 2 13 9 20 9" />
  </svg>
);

const IconLink = () => (
  <svg {...iconProps}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

/**
 * The checks the form schema cannot do on its own, all of them fatal to what
 * they configure, hence refused at save time:
 * - free text executed inside the WebRTC negotiation (SDP pipelines, dry-run
 *   here) and fields that are only wrong relative to each other (a source's
 *   x-google bitrates) — both would kill a publication;
 * - a composition whose grid template CSS would reject as a whole, or whose
 *   slots have nowhere to go — the page would come up blank.
 */
const blockingErrorLines = (settings: any): string[] => [
  ...validateSdpFormatterSettings(settings?.emission?.sdpFormatter)
    .map(({ field, message }) => `emission.sdpFormatter.${field}: ${message}`),
  ...validateEmissionSettings(settings).map(({ path, message }) => `${path}: ${message}`),
  ...validateCompositionSettings(settings).map(({ path, message }) => `${path}: ${message}`),
];

interface JsonViewStatus {
  errors: string[];
  warnings: string[];
  /** Form-shaped settings (sanitized) — only set when `errors` is empty. */
  sanitized?: any;
}

// ---- Prompt popup used by URL / paste imports and config creation ----

interface PromptPopupProps {
  title: string;
  description?: string;
  placeholder?: string;
  multiline?: boolean;
  initialValue?: string;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (value: string) => Promise<void>;
}

const PromptPopup = ({ title, description, placeholder, multiline, initialValue, submitLabel, onCancel, onSubmit }: PromptPopupProps) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!value.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(value);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className={styles.promptWrapper}>
      <BoxContainer
        topBarProps={{
          left: [],
          center: [<span key="title" className={workBoxStyles.label}>{title}</span>],
          right: [
            <PrimaryButton key="close" onClick={onCancel}>
              <ReactIcons.Close style={{ height: "0.8em" }} />
            </PrimaryButton>,
          ],
        }}
      >
        <div className={styles.promptContent}>
          {description ? <div className={styles.promptDescription}>{description}</div> : null}
          {multiline ? (
            <textarea
              className={styles.promptTextarea}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
              spellCheck={false}
            />
          ) : (
            <input
              className={styles.promptInput}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
              spellCheck={false}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          )}
        </div>
        <div className={styles.promptFooter}>
          <Button.Simple label="Annuler" onClick={onCancel} />
          <Button.Simple label={busy ? "…" : submitLabel} onClick={submit} />
        </div>
      </BoxContainer>
    </div>
  );
};

export interface SettingsPopupProps {}

export interface SettingsPopupRef {
  openPopup: () => void;
}

const SettingsPopup = React.forwardRef(({}: SettingsPopupProps, forwardRef: ForwardedRef<SettingsPopupRef | undefined>) => {
  const requestAdminPin = useRequestAdminPin();
  const [isOpen, setIsOpen] = useState(false);

  const settingsFormRef = useRef<SettingsFormRef | null>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const variablesFileInputRef = useRef<HTMLInputElement>(null);
  const formValidRef = useRef(true);
  const formErrorsRef = useRef<{ path: string; message: string }[]>([]);

  // JSON view (toggled by the header slider) — edits the real settings shape.
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const jsonStatus = useMemo<JsonViewStatus | undefined>(() => {
    if (!jsonMode) return undefined;
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      return { errors: [formatJsonParseError(err, jsonText)], warnings: [] };
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { errors: ["Le JSON doit être un objet de paramètres"], warnings: [] };
    }
    // `_schemaVersion` is the export marker, not a setting: pasting an exported
    // file must not report it as an unknown field (the import path strips it too).
    const { _schemaVersion, ...data } = parsed;
    const { sanitized, warnings } = sanitizeSettings(data, settingsSchema);
    return { errors: blockingErrorLines(sanitized), warnings, sanitized };
  }, [jsonMode, jsonText]);

  // Multi-config: the popup views/edits one config at a time; a different one
  // may stay "active" (used by the app) until the check marks the viewed one.
  const [configs, setConfigs] = useState<SettingsConfigInfo[]>([]);
  const [viewedKey, setViewedKey] = useState<string>(() => resolveActiveSettingsKey());
  // Baseline of the viewed config as loaded — used to detect unsaved edits.
  const loadedDataRef = useRef<Settings>({});

  // Whether the active config changed (saved or re-selected) while the
  // settings were open — drives the "refresh the page?" proposal on leave.
  const activeTouchedRef = useRef(false);

  const viewedConfig = configs.find((c) => c.key === viewedKey);
  const viewedName = viewedConfig?.name ?? viewedKey;

  // A remote config takes its content from a URL: the editor only displays it
  // (read-only), and what the popup edits is the source — mode and URL.
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  useEffect(() => subscribeRemoteSettingsStatus(forceRender), []);
  const isRemote = viewedConfig?.mode === "remote";
  const remoteSource = isRemote ? getSettingsConfigSource(viewedKey) : undefined;
  const remoteStatus = isRemote ? getRemoteSettingsStatus(viewedKey) : undefined;
  // Variables have their own source: local values, or a `{ "NAME": "value" }` URL.
  const variablesSource = getSettingsConfigSource(viewedKey);
  const variablesRemote = variablesSource.variablesMode === "remote";
  const variablesStatus = variablesRemote ? getRemoteVariablesStatus(viewedKey) : undefined;

  // Variables panel (remote only): local values feeding the `[[NAME]]`
  // placeholders of the template. Edited here, applied to the preview on blur.
  const [variables, setVariables] = useState<SettingsVariable[]>([]);
  // Blur commits what is on screen: reading the state through a ref keeps the
  // last keystroke from being dropped when blur fires before the re-render.
  const variablesRef = useRef<SettingsVariable[]>([]);
  variablesRef.current = variables;
  /**
   * Whether the editor shows the config with its variables applied. A remote
   * config is displayed resolved by default (it is read-only anyway); a local
   * one is edited as a template, and switching to the resolved view turns the
   * editor read-only — saving it would bake the values into the template.
   */
  const [previewResolved, setPreviewResolved] = useState(false);
  // Bumped (debounced) on form edits so the placeholder counters keep up.
  const [formTick, setFormTick] = useState(0);
  const formTickTimer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const [showVariables, setShowVariables] = useState(false);
  /** Remote content, or a resolved preview: what is displayed is not editable. */
  const readOnly = isRemote || previewResolved;


  const tryOpenPopup = () => {
    requestAdminPin((success) => {
      if (!success) return;
      activeTouchedRef.current = false;
      const active = resolveActiveSettingsKey();
      const remote = getSettingsConfigSource(active).mode === "remote";
      const activeVariables = getSettingsConfigVariables(active);
      const formShape = prepareSettingsForForm(configDisplayData(active, remote), settingsSchema);
      loadedDataRef.current = restoreSettingsFromForm(formShape, settingsSchema);
      setViewedKey(active);
      setVariables(activeVariables);
      setPreviewResolved(remote);
      setShowVariables(remote || !!activeVariables.length || hasSettingsPlaceholder(getSettingsConfigData(active)));
      setConfigs(listSettingsConfigs());
      setJsonMode(false);
      setJsonText("");
      setIsOpen(true);
    });
  };

  const showErrorPopup = (title: string, content: React.ReactNode) => {
    showPopup({
      title,
      iconTitle: PopupIconType.ERROR,
      // Import/network errors are multi-line (cause, then what to check).
      content: typeof content === "string" ? <div className={styles.errorMessage}>{content}</div> : content,
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const showErrorsListPopup = (title: string, errors: React.ReactNode[]) => {
    showErrorPopup(
      title,
      <div className={styles.warningsContent}>
        <div style={{ marginBottom: 8 }}>Corrigez les erreurs suivantes :</div>
        <div className={styles.warningsList}>
          {errors.map((e, i) => (
            <div key={i} className={styles.warningItem}>• {e}</div>
          ))}
        </div>
      </div>,
    );
  };

  const showWarningsPopup = (title: string, warnings: string[], onContinue: () => void) => {
    showPopup({
      title,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div className={styles.warningsContent}>
          <div className={styles.warningsList}>
            {warnings.map((w, i) => (
              <div key={i} className={styles.warningItem}>• {w}</div>
            ))}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Annuler" },
        {
          type: PopupButtonType.VALIDATE,
          element: <div style={{ color: Colors.getMainGreen() }}>Continuer quand même</div>,
        },
      ],
      onClose: (data: PopupOnCloseData) => {
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          onContinue();
        }
      },
    });
  };

  /** Current settings in their real (persisted) shape, whatever the active view. */
  const getCurrentSettingsData = (): Settings => {
    if (jsonMode) {
      if (!jsonStatus || jsonStatus.errors.length) {
        throw new Error(jsonStatus?.errors.join(" — ") || "JSON invalide");
      }
      return restoreSettingsFromForm(jsonStatus.sanitized, settingsSchema);
    }
    const currentValue = settingsFormRef.current?.getValue() ?? {};
    return restoreSettingsFromForm(currentValue, settingsSchema);
  };

  // Write the viewed config to localStorage only — the refresh proposal
  // happens when leaving, and only if the active config was touched.
  const doSave = (): boolean => {
    try {
      const data = getCurrentSettingsData();
      saveSettingsConfigData(viewedKey, data);
      loadedDataRef.current = data;
      if (viewedKey === resolveActiveSettingsKey()) activeTouchedRef.current = true;
      toastSuccess(`Configuration « ${viewedName} » sauvegardée`);
      return true;
    } catch (err) {
      showErrorPopup(
        "Impossible de sauvegarder les paramètres",
        "Une erreur est apparue durant la sauvegarde des paramètres",
      );
      console.error("Error while saving parameters : ", err)
      return false;
    }
  };

  const showRefreshPopup = () => {
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Voulez-vous rafraîchir la page pour appliquer vos modifications ?"
              level="success"
              buttons={[
                { label: "Non", onClick: () => removePopup(popup) },
                { label: "Oui, rafraîchir", style: { backgroundColor: "var(--color-green-1)" }, onClick: () => { removePopup(popup); window.location.reload(); } },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_apply_changes", "settings");
  };

  const closeSettings = () => {
    setIsOpen(false);
    if (activeTouchedRef.current) showRefreshPopup();
  };

  // Saving is forbidden while the active view holds errors — list them instead.
  const trySave = (): boolean => {
    if (jsonMode) {
      if (!jsonStatus || jsonStatus.errors.length) {
        showErrorsListPopup("Impossible de sauvegarder", jsonStatus?.errors ?? ["JSON invalide"]);
        return false;
      }
      return true;
    }
    if (!formValidRef.current) {
      showErrorsListPopup(
        "Impossible de sauvegarder",
        formErrorsRef.current.map((e) => (<><strong>{e.path}</strong>: {e.message}</>)),
      );
      return false;
    }
    // Checked here rather than on every keystroke: the form schema has no hook
    // for a custom validation, and a half-typed pipeline (or a min typed before
    // its max) would report constantly.
    const errors = blockingErrorLines(restoreSettingsFromForm(settingsFormRef.current?.getValue() ?? {}, settingsSchema));
    if (errors.length) {
      showErrorsListPopup("Impossible de sauvegarder", errors);
      return false;
    }
    return true;
  };

  // Saving keeps the settings open; `afterSave` carries the follow-up of the
  // flow that required saving first (closing, switching config).
  const save = (options?: { afterSave?: () => void }) => {
    if (!trySave()) return;
    const commit = () => {
      if (doSave()) options?.afterSave?.();
    };
    if (jsonMode && jsonStatus?.warnings.length) {
      showWarningsPopup("Avertissements avant sauvegarde", jsonStatus.warnings, commit);
    } else {
      commit();
    }
  };

  // ---- Multi-config management ----

  /** Load a config into the active view (form and, when shown, JSON editor). */
  /**
   * What the editor shows: the config as stored, except a remote one displayed
   * with its variables resolved (the "raw" toggle shows the template instead).
   */
  const configDisplayData = (key: string, resolved = previewResolved): Settings => (
    resolved ? getSettingsConfigResolvedData(key).settings : getSettingsConfigData(key)
  );

  /** Push a config into the editor (form and, when shown, JSON view). */
  const showConfigData = (key: string, resolved = previewResolved) => {
    const formShape = prepareSettingsForForm(configDisplayData(key, resolved), settingsSchema);
    settingsFormRef.current?.setValue(JSON.stringify(formShape));
    const restored = restoreSettingsFromForm(formShape, settingsSchema);
    if (jsonMode) setJsonText(JSON.stringify(restored, null, 2));
    loadedDataRef.current = restored;
  };

  const loadConfig = (key: string) => {
    const remote = getSettingsConfigSource(key).mode === "remote";
    const configVariables = getSettingsConfigVariables(key);
    showConfigData(key, remote);
    setPreviewResolved(remote);
    setVariables(configVariables);
    // Opened on a config that already uses variables: show the panel straight away.
    setShowVariables(remote || !!configVariables.length || hasSettingsPlaceholder(getSettingsConfigData(key)));
    setViewedKey(key);
  };

  const isViewDirty = (): boolean => {
    if (readOnly) return false; // read-only: the editor cannot hold unsaved edits
    try {
      return !deepEqual(getCurrentSettingsData(), loadedDataRef.current);
    } catch (err) {
      return true; // unparseable JSON view — treat as unsaved edits
    }
  };

  const switchConfig = (key: string) => {
    if (key === viewedKey) return;
    if (!isViewDirty()) {
      loadConfig(key);
      return;
    }
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Des modifications ne sont pas sauvegardées. Que faire avant de passer à « ${configs.find((c) => c.key === key)?.name ?? key} » ?`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                { label: "Ne pas sauvegarder", onClick: () => { removePopup(popup); loadConfig(key); } },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => { removePopup(popup); save({ afterSave: () => loadConfig(key) }); },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_switch_config", "settings");
  };

  /** The check in the config bar: make the viewed config the active one. */
  const handleSelectViewed = () => {
    if (viewedConfig?.selected) return; // cannot be unchecked — select another one instead
    selectSettingsConfig(viewedKey);
    setConfigs(listSettingsConfigs());
    activeTouchedRef.current = true;
    toastSuccess(`Configuration « ${viewedName} » activée`);
  };

  /** Delete the viewed config (confirmation first) and fall back to the default view. */
  const handleDeleteViewed = () => {
    const name = viewedName;
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Supprimer la configuration « ${name} » ? Cette action est définitive.`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Supprimer",
                  style: { backgroundColor: "var(--color-red-1)" },
                  onClick: () => {
                    removePopup(popup);
                    try {
                      const wasActive = !!viewedConfig?.selected;
                      deleteSettingsConfig(viewedKey);
                      if (wasActive) activeTouchedRef.current = true; // the default took over
                      setConfigs(listSettingsConfigs());
                      loadConfig(SETTINGS_DEFAULT_KEY);
                      toastSuccess(`Configuration « ${name} » supprimée`);
                    } catch (err) {
                      showErrorPopup("Impossible de supprimer la configuration", (err as Error).message);
                    }
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_delete_config", "settings");
  };

  /** Empty the viewed config in the editor — takes effect once saved. */
  const handleClearConfig = () => {
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Vider la configuration « ${viewedName} » ? Le contenu affiché sera remplacé par une configuration vide — sauvegardez pour appliquer.`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Vider",
                  style: { backgroundColor: "var(--color-red-1)" },
                  onClick: () => {
                    removePopup(popup);
                    settingsFormRef.current?.setValue("{}");
                    if (jsonMode) setJsonText("{}");
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_clear_config", "settings");
  };

  const openCreateConfigPrompt = () => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title="Nouvelle configuration"
            description="La configuration sera créée vide. Le nom sera normalisé (minuscules, espaces remplacés par des tirets)."
            placeholder="ex: Salle Averroès"
            submitLabel="Créer"
            onCancel={close}
            onSubmit={async (name) => {
              try {
                const info = createSettingsConfig(name, {});
                close();
                setConfigs(listSettingsConfigs());
                switchConfig(info.key); // guards unsaved edits before viewing the empty config
                toastSuccess(`Configuration « ${info.name} » créée`);
              } catch (err) {
                showErrorPopup("Impossible de créer la configuration", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_create_config", "settings");
  };

  // ---- Remote configs (mode + URL) ----

  /** Fetch a remote config now; the viewed editor then shows what was loaded. */
  const reloadRemote = async (key: string) => {
    const result = await loadRemoteSettingsConfig(key);
    setConfigs(listSettingsConfigs());
    if (key === viewedKey) loadConfig(key);
    if (key === resolveActiveSettingsKey()) activeTouchedRef.current = true;
    if (result.ok) toastSuccess("Configuration distante rechargée");
    else showErrorPopup("Impossible de charger la configuration distante", result.error ?? "Erreur inconnue");
    return result.ok;
  };

  /**
   * Ask for the URL and check it before adopting it: an unreachable URL or an
   * invalid file is reported here, and the config stays as it is.
   */
  const openRemoteUrlPrompt = (options: { title: string; description: string }) => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    const adopt = (url: string) => {
      setSettingsConfigSource(viewedKey, { mode: "remote", url });
      close();
      reloadRemote(viewedKey);
    };
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title={options.title}
            description={options.description}
            placeholder="https://…/settings.json"
            initialValue={remoteSource?.url}
            submitLabel="Valider"
            onCancel={close}
            onSubmit={async (url) => {
              try {
                const { warnings, missing } = await checkRemoteSettingsUrl(url, getSettingsConfigVariables(viewedKey));
                const all = [
                  ...warnings,
                  ...(missing.length
                    ? [`Variables non définies : ${missing.join(", ")} — renseignez-les dans le panneau « Variables ».`]
                    : []),
                ];
                if (all.length) showWarningsPopup("Avertissements sur la configuration distante", all, () => adopt(url));
                else adopt(url);
              } catch (err) {
                showErrorPopup("URL invalide", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_remote_url", "settings");
  };

  const switchToLocal = () => {
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Repasser « ${viewedName} » en configuration locale ? Le dernier contenu chargé depuis l'URL est conservé (variables comprises) et redevient modifiable.`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Passer en local",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => {
                    removePopup(popup);
                    // Variables are kept: they apply to a local config too.
                    setSettingsConfigSource(viewedKey, { mode: "local", variables: variablesRef.current });
                    setConfigs(listSettingsConfigs());
                    // Reload the view: it was the read-only resolved preview of
                    // a remote config, and the editor is now editable again.
                    loadConfig(viewedKey);
                    if (viewedKey === resolveActiveSettingsKey()) activeTouchedRef.current = true;
                    toastSuccess(`Configuration « ${viewedName} » repassée en local`);
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_source_local", "settings");
  };

  // ---- Variables of a remote config ----

  /**
   * Persist the variables and refresh the preview. Called on blur rather than
   * on every keystroke: applying them re-seeds the whole form (and notifies the
   * app, which rebuilds its sources).
   */
  const commitVariables = (next: SettingsVariable[]) => {
    setVariables(next);
    setSettingsConfigVariables(viewedKey, next);
    if (readOnly) {
      showConfigData(viewedKey); // the resolved view: show the new value at once
    } else if (!isViewDirty()) {
      // Editing a variable while looking at the template shows nothing (the
      // template holds `[[NAME]]`, not the value): switch to the resolved view,
      // which is what the change is about. Skipped when the editor holds
      // unsaved edits — re-seeding it would drop them.
      applyPreviewMode(true);
    }
    if (viewedKey === resolveActiveSettingsKey()) activeTouchedRef.current = true;
  };

  const addVariable = (name = "") => {
    const next = [...variablesRef.current, { name, value: "" }];
    setVariables(next);
    if (name) commitVariables(next); // defining a placeholder: nothing to type first
  };

  const removeVariable = (index: number) => {
    commitVariables(variablesRef.current.filter((_, i) => i !== index));
  };

  const editVariable = (index: number, patch: Partial<SettingsVariable>) => {
    setVariables(variablesRef.current.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  };

  /** Fetch the variables file now; the panel and the preview follow. */
  const reloadRemoteVariables = async (key: string) => {
    const result = await loadRemoteSettingsVariables(key);
    if (key === viewedKey) {
      setVariables(getSettingsConfigVariables(key));
      if (readOnly) showConfigData(key);
    }
    if (key === resolveActiveSettingsKey()) activeTouchedRef.current = true;
    if (result.ok) toastSuccess("Variables rechargées");
    else showErrorPopup("Impossible de charger les variables distantes", result.error ?? "Erreur inconnue");
  };

  /** Ask for the variables URL and check it before adopting it. */
  const openVariablesUrlPrompt = () => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title="Variables distantes"
            description={'URL d\'un fichier JSON { "NOM": "valeur" } — le format produit par « Exporter ». Elle est vérifiée maintenant, puis rechargée à chaque lancement. Les variables locales actuelles seront remplacées par celles de l\'URL.'}
            placeholder="https://…/variables.json"
            initialValue={variablesSource.variablesUrl}
            submitLabel="Valider"
            onCancel={close}
            onSubmit={async (url) => {
              try {
                await checkRemoteVariablesUrl(url);
                setSettingsConfigVariablesUrl(viewedKey, url);
                close();
                reloadRemoteVariables(viewedKey);
              } catch (err) {
                showErrorPopup("URL de variables invalide", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_variables_url", "settings");
  };

  const handleChangeVariablesMode = (mode: SettingsConfigMode) => {
    if (mode === (variablesRemote ? "remote" : "local")) return;
    if (mode === "remote") {
      openVariablesUrlPrompt();
      return;
    }
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Repasser les variables en local ? Les dernières valeurs chargées depuis l'URL sont conservées et redeviennent modifiables."
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Passer en local",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => {
                    removePopup(popup);
                    setSettingsConfigVariablesUrl(viewedKey, undefined);
                    setVariables(getSettingsConfigVariables(viewedKey));
                    if (viewedKey === resolveActiveSettingsKey()) activeTouchedRef.current = true;
                    toastSuccess("Variables repassées en local");
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_variables_source_local", "settings");
  };

  /**
   * Variables as a hand-editable `{ "NAME": "value" }` file. Placeholders with
   * no value are exported as `null`: the file then lists everything the config
   * expects, ready to be filled in and imported on another machine.
   */
  const handleExportVariables = () => {
    const data: Record<string, string | null> = {};
    variablesRef.current.forEach(({ name, value }) => {
      if (name.trim()) data[name.trim()] = value;
    });
    Object.keys(occurrences).forEach((name) => {
      if (!(name in data)) data[name] = null;
    });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `variables-${viewedName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Imported names overwrite the matching variables; the others are kept. */
  const handleVariablesFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const parsed = JSON.parse(await file.text());
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error('Le fichier doit être un objet { "NOM": "valeur" }');
      }
      const next = [...variablesRef.current];
      let applied = 0;
      Object.entries(parsed).forEach(([name, value]) => {
        // `null` marks a variable left to define (that is how they are
        // exported): it must not overwrite a value this machine already has.
        if (value === null || value === undefined) return;
        // A hand-written `true` / `4000` is kept as the text that yields it back.
        const raw = typeof value === "string" ? value : JSON.stringify(value);
        const index = next.findIndex((v) => v.name.trim() === name.trim());
        if (index >= 0) next[index] = { name, value: raw };
        else next.push({ name, value: raw });
        applied += 1;
      });
      commitVariables(next);
      toastSuccess(`${applied} variable${applied > 1 ? "s" : ""} importée${applied > 1 ? "s" : ""}`);
    } catch (err) {
      showErrorPopup("Erreur d'import des variables", (err as Error).message);
    }
  };

  const applyPreviewMode = (resolved: boolean) => {
    setPreviewResolved(resolved);
    showConfigData(viewedKey, resolved);
  };

  /**
   * Switching view re-seeds the editor from storage, so unsaved edits of a
   * local config are guarded first.
   */
  const handleTogglePreview = (resolved: boolean) => {
    if (!isViewDirty()) {
      applyPreviewMode(resolved);
      return;
    }
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Des modifications ne sont pas sauvegardées. Que faire avant de changer de vue ?"
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                { label: "Ne pas sauvegarder", onClick: () => { removePopup(popup); applyPreviewMode(resolved); } },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => { removePopup(popup); save({ afterSave: () => applyPreviewMode(resolved) }); },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_preview_mode", "settings");
  };

  const handleChangeMode = (mode: SettingsConfigMode) => {
    if (mode === (isRemote ? "remote" : "local")) return;
    if (mode === "local") {
      switchToLocal();
      return;
    }
    openRemoteUrlPrompt({
      title: "Configuration distante",
      description:
        "URL du fichier JSON de paramètres (ou lien de l'application contenant « ?settings=… »). Elle est vérifiée maintenant, puis rechargée à chaque lancement de l'application. Le contenu local actuel de cette configuration sera remplacé par celui de l'URL.",
    });
  };

  /**
   * Read-only enforcement on a displayed-only config: CSS alone would still let
   * the keyboard (tab + space / typing) edit the fields. Text fields become
   * `readOnly` (they stay selectable, so values can be copied), and the ones
   * that have no read-only state — checkboxes, radios, selects — are disabled.
   * Re-applied on every DOM change: the form mounts fields as sections expand.
   * Locked fields are tagged, so leaving the read-only view unlocks exactly
   * those (and not a field the library disabled on its own).
   */
  useEffect(() => {
    const root = formContainerRef.current;
    if (!root) return;
    const fields = () => root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select");
    if (!readOnly) {
      fields().forEach((el) => {
        if (!el.dataset.settingsLocked) return;
        if (el.dataset.settingsLocked === "readonly") (el as HTMLInputElement).readOnly = false;
        else el.disabled = false;
        delete el.dataset.settingsLocked;
      });
      return;
    }
    const lock = () => {
      fields().forEach((el) => {
        if (el.dataset.settingsLocked) return;
        const type = (el as HTMLInputElement).type;
        if (el.tagName === "SELECT" || type === "checkbox" || type === "radio" || type === "file" || type === "color") {
          if (el.disabled) return; // already disabled by the form itself
          el.disabled = true;
          el.dataset.settingsLocked = "disabled";
        } else {
          if ((el as HTMLInputElement).readOnly) return;
          (el as HTMLInputElement).readOnly = true;
          el.dataset.settingsLocked = "readonly";
        }
      });
    };
    lock();
    // childList only: the attributes we set here would otherwise re-trigger it.
    const observer = new MutationObserver(lock);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [readOnly, viewedKey, jsonMode, previewResolved]);

  const handleFormChange = useCallback((data: SettingsFormChangeData) => {
    formValidRef.current = data.valid;
    formErrorsRef.current = data.errors;
    // Placeholder counters follow what is typed, without re-rendering per keystroke.
    if (formTickTimer.current) clearTimeout(formTickTimer.current);
    formTickTimer.current = setTimeout(() => setFormTick((t) => t + 1), 300);
  }, []);

  useEffect(() => () => { if (formTickTimer.current) clearTimeout(formTickTimer.current); }, []);

  // ---- View toggle (form <-> JSON) ----

  const handleToggleJsonMode = (next: boolean) => {
    if (next) {
      try {
        const currentValue = settingsFormRef.current?.getValue() ?? {};
        const restored = restoreSettingsFromForm(currentValue, settingsSchema);
        setJsonText(JSON.stringify(restored, null, 2));
        setJsonMode(true);
      } catch (err) {
        showErrorPopup("Vue JSON", "Impossible de générer la vue JSON : " + (err as any)?.message);
      }
    } else {
      if (!jsonStatus || jsonStatus.errors.length) {
        showErrorsListPopup("Impossible de revenir au formulaire", jsonStatus?.errors ?? ["JSON invalide"]);
        return;
      }
      settingsFormRef.current?.setValue(JSON.stringify(jsonStatus.sanitized));
      setJsonMode(false);
    }
  };

  const handleReformatJson = () => {
    try {
      setJsonText(JSON.stringify(JSON.parse(jsonText), null, 2));
    } catch (err) { /* invalid JSON — nothing to reformat */ }
  };

  // ---- Export / copy ----

  const handleExport = () => {
    try {
      exportSettings(getCurrentSettingsData());
    } catch (err) {
      showErrorPopup("Erreur d'export", "Impossible d'exporter les paramètres : " + (err as any)?.message);
    }
  };

  // Copy the current content (including unsaved edits) in one of the
  // forms accepted by the `?settings=` URL query.
  const handleCopy = (encode?: (json: string) => string) => {
    try {
      const json = JSON.stringify(getCurrentSettingsData());
      const payload = encode ? encode(json) : json;
      navigator.clipboard.writeText(payload).then(() => {
        toastSuccess("Copié dans le presse-papier");
      }).catch((err) => {
        toastError("Impossible de copier dans le presse-papier");
        console.error("Failed to copy settings: ", err);
      });
    } catch (err) {
      showErrorPopup("Erreur de copie", "Impossible de copier les paramètres : " + (err as any)?.message);
    }
  };

  // ---- Import ----

  const applyImportResult = (result: ImportResult) => {
    const allWarnings: string[] = [];
    if (result.versionWarning) allWarnings.push(result.versionWarning);
    allWarnings.push(...result.warnings);

    const apply = () => {
      settingsFormRef.current?.setValue(JSON.stringify(result.settings));
      if (jsonMode) {
        const restored = restoreSettingsFromForm(result.settings, settingsSchema);
        setJsonText(JSON.stringify(restored, null, 2));
      }
      toastSuccess("Paramètres importés");
    };

    if (allWarnings.length > 0) {
      showWarningsPopup("Avertissements à l'import", allWarnings, apply);
    } else {
      apply();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      applyImportResult(await importSettings(file));
    } catch (err) {
      showErrorPopup("Erreur d'import", (err as Error).message);
    }
  };

  const openImportPrompt = (options: {
    title: string;
    description?: string;
    placeholder?: string;
    multiline?: boolean;
    importFrom: (value: string) => Promise<ImportResult> | ImportResult;
  }) => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title={options.title}
            description={options.description}
            placeholder={options.placeholder}
            multiline={options.multiline}
            submitLabel="Importer"
            onCancel={close}
            onSubmit={async (value) => {
              try {
                const result = await options.importFrom(value);
                close();
                applyImportResult(result);
              } catch (err) {
                showErrorPopup("Erreur d'import", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_import_prompt", "settings");
  };

  const handleClose = () => {
    if (!isViewDirty()) {
      closeSettings();
      return;
    }
    let popup: React.ReactNode;
    const dismiss = () => { removePopup(popup); closeSettings(); };
    const closePopupOnly = () => { removePopup(popup); };
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={closePopupOnly}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Voulez-vous sauvegarder avant de fermer ?"
              level="question"
              buttons={[
                {
                  label: "Non, fermer",
                  onClick: dismiss,
                },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => {
                    removePopup(popup);
                    save({ afterSave: closeSettings });
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_save_confirm", "settings");
  };

  // Placeholder counters: from what is being edited when the editor is live,
  // from the stored config when it only displays it.
  const occurrences = useMemo(() => {
    try {
      return countSettingsVariableOccurrences(readOnly ? getSettingsConfigData(viewedKey) : getCurrentSettingsData());
    } catch (err) {
      return countSettingsVariableOccurrences(getSettingsConfigData(viewedKey)); // unparseable JSON view
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, viewedKey, variables, jsonMode, jsonText, formTick]);

  const undefinedVariables = useMemo(() => {
    const defined = new Set(variables.map((v) => v.name.trim()).filter(Boolean));
    return Object.entries(occurrences).filter(([name]) => !defined.has(name));
  }, [occurrences, variables]);

  useImperativeHandle(forwardRef, () => ({
    openPopup: tryOpenPopup,
  }));

  if (!isOpen) return null;

  const jsonHasErrors = !!jsonStatus?.errors.length;
  const jsonHasWarnings = !jsonHasErrors && !!jsonStatus?.warnings.length;

  /** Above the editor: why it is read-only, and the template/resolved toggle. */
  const previewHeader = (
    <div className={styles.previewHeader}>
      {isRemote ? (
        <div className={styles.readOnlyBanner}>
          <span aria-hidden>⚠️</span>
          Configuration distante : le contenu ci-dessous provient de l'URL et n'est pas modifiable ici.
        </div>
      ) : previewResolved ? (
        <div className={styles.readOnlyBanner}>
          <span aria-hidden>⚠️</span>
          Aperçu avec les variables appliquées — revenez au template pour modifier la configuration.
        </div>
      ) : !jsonMode && hasSettingsPlaceholder(getSettingsConfigData(viewedKey)) ? (
        <div className={styles.readOnlyBanner}>
          <span aria-hidden>⚠️</span>
          Cette configuration utilise des variables : le formulaire ne sait pas afficher un
          {" "}<code>[[NOM]]</code> dans un champ typé (booléen, nombre) et le perdrait à la
          sauvegarde. Passez en JSON pour l'éditer.
        </div>
      ) : (
        <div className={styles.previewSpacer} />
      )}
      <Button.Simple
        label={previewResolved ? "Voir le template" : "Voir avec les variables"}
        className={styles.configBarBtn}
        onClick={() => handleTogglePreview(!previewResolved)}
      />
    </div>
  );

  const variablesPanel = (
    <div className={styles.variablesPane}>
      <div className={styles.variablesHeader}>
        <span className={styles.variablesTitle}>Variables</span>
        <div className={styles.variablesActions}>
          {variablesRemote ? null : (
            <Button.Simple label="+ Ajouter" className={styles.variablesActionBtn} onClick={() => addVariable()} />
          )}
          <Button.Simple
            label={<span className={styles.configDeleteLabel}><IconDownload />Exporter</span>}
            className={clsx(styles.variablesActionBtn, styles.exportBtn)}
            onClick={handleExportVariables}
          />
          {variablesRemote ? null : (
            <Button.Simple
              label={<span className={styles.configDeleteLabel}><IconFile />Importer</span>}
              className={clsx(styles.variablesActionBtn, styles.importBtn)}
              onClick={() => variablesFileInputRef.current?.click()}
            />
          )}
          <input
            ref={variablesFileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleVariablesFileSelected as any}
          />
        </div>
      </div>

      {/* Where the values come from — same two sources as the config itself. */}
      <div className={styles.variablesSourceRow}>
        <span className={styles.configBarLabel}>Source :</span>
        <Dropdown
          options={["local", "remote"]}
          selected={variablesRemote ? "remote" : "local"}
          onSelect={(mode) => handleChangeVariablesMode(mode as SettingsConfigMode)}
          getLabel={(mode) => (mode === "remote" ? "Distante (URL)" : "Locale (navigateur)")}
          width="170px"
          className={styles.configDropdown}
        />
        {variablesRemote ? (
          <>
            <Button.Simple label="Modifier l'URL…" className={styles.variablesActionBtn} onClick={openVariablesUrlPrompt} />
            <Button.Simple
              label={variablesStatus?.loading ? "Chargement…" : "Recharger"}
              className={styles.variablesActionBtn}
              onClick={() => { if (!variablesStatus?.loading) reloadRemoteVariables(viewedKey); }}
            />
          </>
        ) : null}
      </div>
      {variablesRemote ? (
        <div className={styles.variablesSourceStatus}>
          <span className={styles.sourceUrl} title={variablesSource.variablesUrl}>{variablesSource.variablesUrl}</span>
          <span
            title={variablesStatus?.error}
            className={clsx(styles.sourceStatus, variablesStatus?.error && styles.sourceStatusError)}
          >
            {variablesStatus?.loading
              ? "…"
              : variablesStatus?.error
                ? `✕ ${variablesStatus.error}`
                : variablesSource.variablesFetchedAt
                  ? `✓ Chargées le ${new Date(variablesSource.variablesFetchedAt).toLocaleString()}`
                  : "Jamais chargées"}
          </span>
        </div>
      ) : null}
      <div className={styles.variablesHint}>
        Chaque <code>[[NOM]]</code> de la configuration est remplacé par la valeur correspondante au chargement.
        {variablesRemote
          ? " Ces valeurs proviennent de l'URL ci-dessous et ne sont pas modifiables ici."
          : " Les valeurs restent sur ce poste."}{" "}
        Une valeur JSON (<code>4000</code>, <code>true</code>) garde son type — entourez-la de guillemets
        (<code>"4000"</code>) pour forcer une chaîne.
      </div>
      {variables.length ? (
        <div className={styles.variablesList}>
          {variables.map((variable, index) => {
            const count = occurrences[variable.name.trim()] ?? 0;
            return (
              <div key={index} className={styles.variableRow}>
                <input
                  className={clsx(styles.variableInput, styles.variableName)}
                  value={variable.name}
                  placeholder="NOM"
                  spellCheck={false}
                  readOnly={variablesRemote}
                  onChange={(e) => editVariable(index, { name: e.target.value })}
                  onBlur={() => commitVariables(variablesRef.current)}
                />
                <input
                  className={clsx(styles.variableInput, styles.variableValue)}
                  value={variable.value}
                  placeholder="valeur"
                  spellCheck={false}
                  readOnly={variablesRemote}
                  onChange={(e) => editVariable(index, { value: e.target.value })}
                  onBlur={() => commitVariables(variablesRef.current)}
                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                />
                <span
                  className={clsx(styles.variableCount, !count && styles.variableCountUnused)}
                  title={count ? `${count} occurrence${count > 1 ? "s" : ""} dans la configuration` : "Aucune occurrence dans la configuration"}
                >
                  {count ? `×${count}` : "0"}
                </span>
                {variablesRemote ? null : (
                  <button
                    type="button"
                    className={styles.variableDelete}
                    title="Supprimer la variable"
                    onClick={() => removeVariable(index)}
                  >
                    <ReactIcons.Trash style={{ height: "0.9em" }} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.variablesEmpty}>
          {variablesRemote ? "Aucune variable chargée depuis cette URL." : "Aucune variable définie."}
        </div>
      )}
      {undefinedVariables.length ? (
        <div className={styles.variablesMissing}>
          <div className={styles.variablesMissingTitle}>
            Utilisées dans la configuration, non définies
          </div>
          {undefinedVariables.map(([name, count]) => (
            <div key={name} className={styles.variableRow}>
              <span title={name} className={clsx(styles.variableInput, styles.variableName, styles.variableMissingName)}>{name}</span>
              <span className={styles.variableCount} title={`${count} occurrence${count > 1 ? "s" : ""}`}>×{count}</span>
              {variablesRemote ? null : (
                <Button.Simple label="Définir" className={styles.variableDefineBtn} onClick={() => addVariable(name)} />
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  /** Form and JSON views — mounted the same way whatever the config source. */
  const editorPanes = (
    <>
      <div
        ref={formContainerRef}
        className={clsx(styles.settingsFormContainer, isRemote && styles.readOnlyPane)}
        style={jsonMode ? { display: "none" } : undefined}
      >
        <SettingsForm
          ref={settingsFormRef}
          schema={settingsSchema}
          defaultValue={JSON.stringify(prepareSettingsForForm(configDisplayData(viewedKey), settingsSchema))}
          onChange={handleFormChange}
          style={{ width: '100%' }}
        />
      </div>
      {jsonMode ? (
        <div className={styles.jsonView}>
          <JsonEditor value={jsonText} onChange={setJsonText} readOnly={readOnly} className={styles.jsonViewEditor} />
          <div
            className={clsx(
              styles.jsonStatusBar,
              jsonHasErrors && styles.jsonStatusBarError,
              jsonHasWarnings && styles.jsonStatusBarWarning,
            )}
          >
            <div className={styles.jsonStatusHeader}>
              <span className={styles.jsonStatusTitle}>
                {jsonHasErrors
                  ? "✕ JSON invalide — sauvegarde impossible"
                  : jsonHasWarnings
                    ? `⚠ JSON valide — ${jsonStatus!.warnings.length} avertissement${jsonStatus!.warnings.length > 1 ? "s" : ""}`
                    : "✓ JSON valide"}
              </span>
              {!jsonHasErrors ? (
                <button type="button" className={styles.jsonStatusAction} onClick={handleReformatJson}>
                  Reformater
                </button>
              ) : null}
            </div>
            {jsonHasErrors || jsonHasWarnings ? (
              <div className={styles.jsonStatusList}>
                {(jsonHasErrors ? jsonStatus!.errors : jsonStatus!.warnings).map((msg, i) => (
                  <div key={i} className={styles.jsonStatusItem}>• {msg}</div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <div className={styles.overlay}>
      {/* Config bar — choose / create / activate a config */}
      <div className={styles.configBar}>
        <span className={styles.configBarLabel}>Configuration :</span>
        <Dropdown
          options={configs.map((c) => c.key)}
          selected={viewedKey}
          onSelect={switchConfig}
          getLabel={(key) => {
            const config = configs.find((c) => c.key === key);
            return config ? `${config.name}${config.selected ? " (active)" : ""}` : key;
          }}
          width="240px"
          className={styles.configDropdown}
        />
        <Button.Simple label="+ Nouvelle configuration" className={styles.configBarBtn} onClick={openCreateConfigPrompt} />
        {viewedKey !== SETTINGS_DEFAULT_KEY ? (
          <Button.Simple
            label={
              <span className={styles.configDeleteLabel}>
                <ReactIcons.Trash style={{ height: "1em" }} />
                Supprimer
              </span>
            }
            className={clsx(styles.configBarBtn, styles.configDeleteBtn)}
            onClick={handleDeleteViewed}
          />
        ) : null}
        <div className={styles.configBarSpacer} />
        <label className={clsx(styles.configActiveCheck, viewedConfig?.selected && styles.configActiveCheckOn)}>
          <input
            type="checkbox"
            checked={!!viewedConfig?.selected}
            disabled={!!viewedConfig?.selected}
            onChange={handleSelectViewed}
          />
          Configuration active
        </label>
      </div>

      {/* Source bar — where the viewed config takes its content from */}
      <div className={styles.sourceBar}>
        <span className={styles.configBarLabel}>Source :</span>
        <Dropdown
          options={["local", "remote"]}
          selected={isRemote ? "remote" : "local"}
          onSelect={(mode) => handleChangeMode(mode as SettingsConfigMode)}
          getLabel={(mode) => (mode === "remote" ? "Distante (URL)" : "Locale (navigateur)")}
          width="200px"
          className={styles.configDropdown}
        />
        {isRemote ? (
          <>
            <span className={styles.sourceUrl} title={remoteSource?.url}>{remoteSource?.url}</span>
            <Button.Simple label="Modifier l'URL…" className={styles.configBarBtn} onClick={() => openRemoteUrlPrompt({
              title: "Modifier l'URL de la configuration",
              description: "L'URL est vérifiée avant d'être adoptée : une URL inaccessible ou un fichier invalide est refusé.",
            })} />
            <Button.Simple
              label={remoteStatus?.loading ? "Chargement…" : "Recharger"}
              className={styles.configBarBtn}
              onClick={() => { if (!remoteStatus?.loading) reloadRemote(viewedKey); }}
            />
            <span
              title={remoteStatus?.error ?? remoteStatus?.warnings?.join("\n")}
              className={clsx(
                styles.sourceStatus,
                remoteStatus?.error && styles.sourceStatusError,
                !remoteStatus?.error && remoteStatus?.warnings?.length && styles.sourceStatusWarning,
              )}
            >
              {remoteStatus?.loading
                ? "…"
                : remoteStatus?.error
                  ? `✕ ${remoteStatus.error}`
                  : remoteSource?.fetchedAt
                    ? `✓ Chargée le ${new Date(remoteSource.fetchedAt).toLocaleString()}${remoteStatus?.warnings?.length ? ` — ${remoteStatus.warnings.length} avertissement(s)` : ""}`
                    : "Jamais chargée"}
            </span>
          </>
        ) : (
          <span className={styles.sourceStatus}>Contenu stocké dans ce navigateur et modifiable ici.</span>
        )}
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          Paramètres
          <span className={styles.headerConfigName}>{viewedName}</span>
          <span className={styles.headerVersion}>v{SETTINGS_SCHEMA_VERSION}</span>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.viewToggle}>
            <span className={clsx(styles.viewToggleLabel, !jsonMode && styles.viewToggleLabelActive)}>Formulaire</span>
            <Switch value={jsonMode} onChange={handleToggleJsonMode} />
            <span className={clsx(styles.viewToggleLabel, jsonMode && styles.viewToggleLabelActive)}>JSON</span>
          </div>
          <Button.Simple
            label={`Variables${variables.length ? ` (${variables.length})` : ""}`}
            className={clsx(styles.actionBtn, showVariables && styles.actionBtnActive)}
            onClick={() => setShowVariables(!showVariables)}
          />
          <Button.Simple
            label="Media Devices"
            className={styles.actionBtn}
            onClick={async () => {
              const devices = await getMediaDevices("both", "input");
              let popup: React.ReactNode;
              popup = (
                <OverlayContainer
                  visible
                  style={{ width: "100dvw", height: "100dvh" }}
                  onClose={() => removePopup(popup)}
                  overlay={
                    <div onClick={(e) => e.stopPropagation()} className={styles.devicesWorkBoxWrapper}>
                      <BoxContainer
                        topBarProps={{
                          left: [],
                          center: [<span key="title" className={workBoxStyles.label}>Media Devices</span>],
                          right: [
                            <PrimaryButton key="close" onClick={() => removePopup(popup)}>
                              <ReactIcons.Close style={{ height: "0.8em" }} />
                            </PrimaryButton>,
                          ],
                        }}
                      >
                        <div className={styles.devicesWorkBoxContent}>
                          <pre className={styles.devicesWorkBoxPre} dangerouslySetInnerHTML={{ __html: syntaxHighlightJson(devices) }} />
                        </div>
                        <div className={styles.devicesWorkBoxFooter}>
                          <Button.Simple label="Fermer" onClick={() => { removePopup(popup); }} />
                        </div>
                      </BoxContainer>
                    </div>
                  }
                />
              );
              addPopup(popup, "settings_media_devices", "settings");
            }}
          />
          <ActionMenu
            label={<span className={styles.configDeleteLabel}><IconDownload />Exporter</span>}
            buttonClassName={clsx(styles.actionBtn, styles.exportBtn)}
            items={[
              { icon: <IconDownload />, label: "Télécharger (.json)", onClick: handleExport },
              { icon: <IconClipboard />, label: "Copier JSON", onClick: () => handleCopy() },
              { icon: <IconClipboard />, label: "Copier JSON échappé (URL)", onClick: () => handleCopy(encodeURIComponent) },
              { icon: <IconClipboard />, label: "Copier base64url", onClick: () => handleCopy(encodeBase64Url) },
            ]}
          />
          {readOnly ? null : (
          <ActionMenu
            label={<span className={styles.configDeleteLabel}><IconFile />Importer</span>}
            buttonClassName={clsx(styles.actionBtn, styles.importBtn)}
            items={[
              { icon: <IconFile />, label: "Depuis un fichier…", onClick: () => fileInputRef.current?.click() },
              {
                icon: <IconLink />,
                label: "Depuis une URL…",
                onClick: () => openImportPrompt({
                  title: "Importer depuis une URL",
                  description: "URL d'un fichier JSON de paramètres, ou lien de l'application contenant « ?settings=… ».",
                  placeholder: "https://…",
                  importFrom: importSettingsFromUrl,
                }),
              },
              {
                icon: <IconClipboard />,
                label: "Coller JSON / base64…",
                onClick: () => openImportPrompt({
                  title: "Importer JSON / base64",
                  description: "Collez un JSON de paramètres, ou sa version encodée en base64 / base64url.",
                  placeholder: '{ "roomId": "…" }  ou  eyJyb29tSWQi…',
                  multiline: true,
                  importFrom: importSettingsFromText,
                }),
              },
            ]}
          />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleFileSelected as any}
          />
        </div>
      </div>

      {/* Body — scrollable form. Seeded from the stored layer: showing the
          resolved view would bake transient env/URL values into localStorage
          on save. The form stays mounted (hidden) in JSON mode so its state
          and ref survive the round-trip. */}
      <div className={clsx(styles.body, jsonMode && styles.bodyJson, showVariables && styles.bodyRemote)}>
        {/* Local: the editor is the body's only child, so the JSON view keeps
            inheriting the body's flex sizing. Remote: it moves into a column,
            with the variables feeding it alongside. */}
        {showVariables ? (
          <div className={styles.previewPane}>
            {previewHeader}
            {editorPanes}
          </div>
        ) : editorPanes}
        {showVariables ? variablesPanel : null}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerVersions}>
          <Versions />
          <AppVersion />
        </div>
        <div className={styles.footerButtons}>
          {readOnly ? null : (
            <Button.Simple
              label={
                <span className={styles.configDeleteLabel}>
                  <ReactIcons.Trash style={{ height: "0.9em" }} />
                  Vider la configuration
                </span>
              }
              className={clsx(styles.actionBtn, styles.configDeleteBtn)}
              onClick={handleClearConfig}
            />
          )}
          <Button.Simple label="Fermer" className={styles.actionBtn} onClick={handleClose} />
          {readOnly ? null : <Button.Simple label="Sauvegarder" className={styles.actionBtn} onClick={() => save()} />}
        </div>
      </div>
    </div>
  );
});

export default SettingsPopup;
